import { reactive, toRefs } from 'vue'
import { Message } from 'view-design'
import { getMediaRejectInfoById, getMediaRejectionList, getElementInfoDetail, updateRecordStatus, batchLimitMedia } from '@/api/material.model'
import { IMAGE_TYPES } from '../../uploading/consts'
const STATUS_ENUM = {
  UNFINISHED: 0,
  PROCESSING: 1,
  FINISHED: 2
}

export const STATUS_LIST = [
  {
    name: '待处理',
    id: STATUS_ENUM.UNFINISHED
  },
  {
    name: '进行中',
    id: STATUS_ENUM.PROCESSING
  },
  {
    name: '已完成',
    id: STATUS_ENUM.FINISHED
  }
]

export const BATCH_ACTION = {
  STATUS: 'status',
  LIMIT_MEDIA: 'limitMedia'
}

const getStatusName = (status) => STATUS_LIST.find((item) => item.id === status)?.name || ''

/**
 * @description: 素材详情的媒体拒审记录
 * @param {*} props
 * @return {*}
 */
export const useMaterialDetailMediaRejection = (props) => {
  const MEDIA_REJ_ACTIVE_NAME = {
    INFO: 'info',
    LIST: 'list'
  }

  const model = reactive({
    mediaRejActiveName: MEDIA_REJ_ACTIVE_NAME.INFO,
    mediaRejLoading: false,
    rejectionData: [],
    mediaRefPager: {
      pageIndex: 1,
      pageSize: 10,
      total: 0
    }
  })

  function onQueryMediaRejInfo() {
    if (model.mediaRejActiveName === MEDIA_REJ_ACTIVE_NAME.LIST) {
      if (model.mediaRejLoading) return
      model.mediaRejLoading = true
      const { total, ...pager } = model.mediaRefPager
      getMediaRejectInfoById({ ...pager, detailId: props.formItem.id }).then(data => {
        const { page_info, list } = data

        if (list?.length) {
          model.rejectionData = list
          model.mediaRefPager.total = page_info.total_number
        } else {
          model.rejectionData = []
          model.mediaRefPager.total = 0
        }
      }).finally(() => {
        model.mediaRejLoading = false
      })
    }
  }

  function onMediaRejSizeChange() {
    model.mediaRefPager.pageIndex = 1
    onQueryMediaRejInfo()
  }
  return {
    MEDIA_REJ_ACTIVE_NAME,
    ...toRefs(model),
    onQueryMediaRejInfo,
    onMediaRejSizeChange
  }
}

/**
 * @description: 素材拒审列表 /material/library/material-media-rejection
 * @return {*}
 */
export const useMediaRejections = (formFields) => {
  const model = reactive({
    mediaRejTableLoading: false,
    imageRejInfoLoading: false,
    tableHeight: 330,
    rejectionData: [],
    selectedRows: [],
    batchDialogVisible: false,
    batchActionType: '',
    operationRows: [],
    initialMediaIds: [],
    operationLoading: false,
    rejTablePager: {
      pageIndex: 1,
      pageSize: 30,
      total: 0
    }
  })

  function buildParams() {
    const { dateRange, recordStatus, ...restParams } = formFields
    const [createBeginTime, createEndTime] = dateRange || []

    return {
      ...restParams,
      createBeginTime,
      createEndTime,
      recordStatus: recordStatus === '' ? undefined : recordStatus
    }
  }

  function onQueryTableData() {
    if (model.mediaRejTableLoading) return
    model.mediaRejTableLoading = true
    model.selectedRows = []
    getMediaRejectionList({
      ...buildParams(),
      pageIndex: model.rejTablePager.pageIndex,
      pageSize: model.rejTablePager.pageSize
    }).then(data => {
      const { page_info, list } = data || {}
      if (list?.length) {
        const imageRejDetail = {}
        model.rejectionData = list.map(i => {
          const elementInfo = i.elementInfo && i.elementInfo.length > 0 ? JSON.parse(i.elementInfo) : {}

          if ([IMAGE_TYPES.GROUP_IMAGE, IMAGE_TYPES.IMAGE].includes(i.type)) {
            if (elementInfo.image_id) {
              if (!imageRejDetail[i.adAccountId]) imageRejDetail[i.adAccountId] = []
              imageRejDetail[i.adAccountId].push(elementInfo.image_id)
            }
          }
          return {
            ...i,
            statusUpdating: false,
            statusVersion: 0,
            imageRejInfo: {},
            elementInfo
          }
        })

        const accountIdKeys = Object.keys(imageRejDetail)
        const imageRejParams = accountIdKeys.map(key => {
          return {
            adAccountId: key,
            imageIds: imageRejDetail[key]
          }
        })

        model.rejTablePager.total = page_info.total_number

        // 批量匹配图片、组图的拒审元素信息
        if (imageRejParams?.length) {
          model.imageRejInfoLoading = true
          getElementInfoDetail({ items: imageRejParams }).then((data) => {
            model.rejectionData = model.rejectionData.map(row => {
              const elementInfo = row.elementInfo
              const imageId = elementInfo.image_id
              let imageRejInfo = {}
              if (imageId) {
                imageRejInfo = data.list?.find(d => d.image_id === imageId && row.adAccountId === d.adAccountId) || {}
              }
              return {
                ...row,
                imageRejInfo
              }
            })
          }).finally(() => {
            model.imageRejInfoLoading = false
          })
        }
      } else {
        model.rejectionData = []
        model.rejTablePager.total = 0
      }
    }).finally(() => {
      model.mediaRejTableLoading = false
    })
  }

  function onSelectionChange(rows) {
    model.selectedRows = rows
  }

  function openBatchDialog(type) {
    if (!model.selectedRows.length) return
    model.batchActionType = type
    model.operationRows = [...model.selectedRows]
    model.initialMediaIds = []
    model.batchDialogVisible = true
  }

  async function onTableRowStatusChange(recordStatus, row) {
    row.statusUpdating = true
    try {
      const data = await updateRecordStatus({ id: row.id, recordStatus })
      if (!data) return
      row.recordStatus = recordStatus
      row.recordStatusName = getStatusName(recordStatus)
      Message.success('状态修改成功')
    } finally {
      row.statusUpdating = false
    }
  }

  async function submitBatchAction({ recordStatus, mediaIds }) {
    const rows = model.operationRows
    const ids = rows.map((row) => row.id)
    if (!ids.length || model.operationLoading) return

    model.operationLoading = true
    try {
      let data
      if (model.batchActionType === BATCH_ACTION.STATUS) {
        data = await updateRecordStatus({ ids: JSON.stringify(ids), recordStatus })
        if (!data) return
        if (data.failedIds?.length) {
          if (data.msg) Message.warning(data.msg)
        } else {
          Message.success('状态修改成功')
        }
      } else {
        data = await batchLimitMedia({ ids: JSON.stringify(ids), excludeMediaIds: mediaIds.join(',') })
        if (!data) return
        Message.success('限制投放媒体修改成功')
      }

      model.batchDialogVisible = false
      model.selectedRows = []
      onQueryTableData()
    } finally {
      model.operationLoading = false
    }
  }
  return {
    ...toRefs(model),
    onQueryTableData,
    onTableRowStatusChange,
    onSelectionChange,
    openBatchDialog,
    submitBatchAction,
    buildParams
  }
}
